/**
 * session-refresh — admin 会话续期单一事实源（access 过期隐式续期 + 三处鉴权 fetch 统一拦截）
 *
 * Why 独立成模块（不并入 client.ts / use-auth.ts）：
 * - refresh 请求必须走**裸 fetch、不经 apiRequest 的 401 拦截器**，否则 refresh 自身 401（refresh
 *   失效）会再次触发刷新 → 死循环。本模块直连 /auth/refresh，与通用拦截彻底解耦。
 * - admin 三处带鉴权的 fetch（client.ts envelope / dashboard 导出 / 通用 CSV 下载）此前各自
 *   「收 401 直接 clearSession 登出」、不续期——这正是「登录约 15min 自动登出」根因。统一收口到
 *   authedFetch 一处「401→refresh→重试一次」，避免散落三份各写一遍又漏掉续期。
 * - 与 use-auth.ts 解耦，避免 client ↔ use-auth 循环依赖。
 *
 * 健壮性（adversarial pre-pass 加固）：
 * - typed 刷新结果：仅 refresh 显式失效(401) 才登出；5xx / 网络 / 解析错 / 超时 视为瞬时故障，
 *   **保留会话**不误登出（瞬时 503 不该把有效会话清掉）。
 * - CAS 防复活/防反吊销：refresh 在途时若登出 / 换号 / 他 tab 已轮换（store refresh token 变化），
 *   丢弃本次结果不 setSession（防陈旧 refresh 复活已清会话 / loser 覆盖 winner 新会话）。
 * - AbortController 超时：refresh 卡死不会让 in-flight 单例永挂、守卫永久 loading。
 * - forceLogout 统一出口：强制登出清登录态 + **清 query 缓存**（部分 query key 不带 userId，
 *   同浏览器换运营不清会读到上一个账户数据，对齐 use-auth.logout / query-client.ts 注释）。
 *
 * 模块级 in-flight 单例：同一 refresh token 的并发调用共享一个 promise、只发一次 /auth/refresh
 * （保留「刷新页面就被登出」修复）。
 *
 * 跨 tab：refresh 一次性轮换、旧 sid 的 access 随之失效，多 tab 各自续期会互相作废 token。故
 * - 续期在 Web Locks 互斥锁内进行，同源所有 tab 串行；
 * - 锁内先以 localStorage 共享会话对齐本 tab：他 tab 已续期就直接用它的 access，不再轮换；
 * - 续期被拒时先等他 tab 的写入经 storage 事件送达（跨进程最终一致），确认没有更新的同会话 token 才判失效。
 * 不支持 Web Locks 的环境退化为不加锁，靠「被拒后等送达」兜并发轮换。
 */

import type { AdminUserResponse } from '@novel-admin/shared';
import { queryClient } from '~/lib/query-client';
import { newRequestId } from '~/lib/request-id';
import { syncAuthFromSharedStorage } from '~/stores/auth-cross-tab-sync';
import { useAuthStore } from '~/stores/auth-store';
import { AdminNetworkError, AdminRequestCanceledError, AdminRequestTimeoutError } from './errors';
import { registerInflightWrite } from './inflight-writes';
import {
  DEFAULT_REQUEST_TIMEOUT_MS,
  composeHardTimeout,
  fetchOrThrowNetworkError,
} from './network-aware-fetch';

function abortError(): DOMException {
  return new DOMException('The operation was aborted.', 'AbortError');
}

/**
 * 等 promise，但 `signal` 触发时立刻 AbortError。
 * 不 abort 那个 promise 本身——refresh 是跨请求共享的 in-flight，取消一个等待者不能掐掉别人的续期。
 */
function raceAbort<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (err) => {
        signal.removeEventListener('abort', onAbort);
        reject(err);
      },
    );
  });
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const REFRESH_PATH = '/api/v1/auth/refresh';
/** refresh 请求硬超时——防卡死网络让 in-flight 单例永挂、守卫永久 loading */
const REFRESH_TIMEOUT_MS = 15_000;
/** 跨 tab 续期互斥锁名（Web Locks，同源所有 tab 共享） */
export const REFRESH_LOCK_NAME = 'novel-admin-auth-refresh';
/**
 * 等锁上限：持锁方最多两次续期（被拒后用共享区新 refresh 再续一次）加 1s 等待窗口；
 * 超出即放弃本次续期（transient，调用方可重试），不让守卫永久 loading
 */
export const REFRESH_LOCK_WAIT_MS = REFRESH_TIMEOUT_MS * 2 + 5_000;
/**
 * 续期被拒后等待他 tab 写入送达的窗口：他 tab 刚轮换时，本 tab 可能抢在 storage 事件送达前
 * 拿已作废的旧 token 续期。只在「被拒」路径上等，会话真失效时登出慢这一拍。
 */
export const SHARED_SESSION_SETTLE_MS = 1_000;

type RefreshData = { access: string; refresh: string; user: AdminUserResponse };

/**
 * 刷新结果（typed）——调用方据此区分「该登出」与「该保留会话重试/报错」：
 * - `ok`：续期成功，store 已写入新 access/refresh。`fromSharedSession` = 未发请求、复用了他 tab 写入共享区的
 *   access（不按本机时钟预判其是否过期），调用方用它被拒时应再真续期一次，而不是判会话失效。
 * - `invalid`：refresh 显式失效（无 refresh token / 端点返 401 且无他 tab 新 token 送达）或他 tab 已登出
 *   → 会话真死，调用方应 forceLogout。
 * - `transient`：5xx / 网络 / 解析错 / 超时 → 会话可能仍有效，**不**登出，由调用方报可重试错误。
 *   其中 `networkFailure=true` 特指「续期请求自身没拿到 HTTP 响应」（断网 / 域名被阻断）：调用方据此
 *   报 `AdminNetworkError`，而不是合成 503 的「服务暂时不可用」——access 每 15min 过期，token 过期
 *   叠加断网是最常见组合，若压成 503 会把本地网络问题说成服务端故障。
 * - `superseded`：在途登出/换号/他 tab 轮换（store refresh token 已变）→ 丢弃结果，不复活不登出。
 */
export type RefreshOutcome =
  | { status: 'ok'; accessToken: string; fromSharedSession?: true }
  | { status: 'invalid' }
  | { status: 'transient'; networkFailure?: boolean }
  | { status: 'superseded' };

// 同一 refresh token 的并发刷新共享同一个 in-flight promise（settle 后自清）
let inFlight: { token: string; promise: Promise<RefreshOutcome> } | null = null;

/** 裸 fetch /auth/refresh（不经 apiRequest 拦截器，防 refresh 自身 401 递归刷新）+ 超时 + 分类 + CAS。 */
async function postRefresh(initiatingRefreshToken: string): Promise<RefreshOutcome> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
  // CAS：发起后 store refresh token 已变（在途登出/换号/重登/他 tab 轮换）→ 不按当前结果动作
  const tokenChanged = (): boolean =>
    useAuthStore.getState().refreshToken !== initiatingRefreshToken;
  try {
    const target = new URL((API_BASE || '') + REFRESH_PATH, window.location.origin);
    let res: Response;
    // networkFailure 的判定必须**只覆盖 fetch 本身**：下方 body 解析 / setSession 持久化
    // （localStorage 被拒或超配额）同样会抛，但那时 HTTP 响应已经拿到了——若一并标成
    // networkFailure，调用方会把「存储写不进去」报成「无法连接服务器」（codex P2）。
    try {
      res = await fetch(target.toString().replace(window.location.origin, ''), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Request-Id': newRequestId() },
        body: JSON.stringify({ refresh: initiatingRefreshToken }),
        signal: controller.signal,
      });
    } catch (err) {
      // abort（超时）不算「连不上」——服务端可能只是慢
      const aborted = (err as { name?: string } | null)?.name === 'AbortError';
      return { status: 'transient', networkFailure: !aborted };
    }
    // 4xx（除 429 限流）= refresh token 失效/格式非法（401 / 422 校验错 / 400），不会自愈 → invalid（清掉坏会话，
    // 防 localStorage 损坏的坏 token 让 RequireAuth 永久重试）。CAS：旧 token 的 4xx 不该清新会话 → superseded。
    if (res.status >= 400 && res.status < 500 && res.status !== 429) {
      return tokenChanged() ? { status: 'superseded' } : { status: 'invalid' };
    }
    // body 解析仍在 timer 覆盖下——body 卡死会被 abort（→ catch → transient），避免 inFlight 永挂、守卫永 loading
    const body = (await res.json().catch(() => null)) as {
      code?: string;
      data?: RefreshData;
    } | null;
    // 5xx / 429 / 解析失败 → 瞬时故障（不登出）
    if (!res.ok || !body || body.code !== 'OK' || !body.data) return { status: 'transient' };
    // 写回前先以共享区对齐：在途期间他 tab 的登出 / 换号写入可能尚未经 storage 事件送达，
    // 不对齐就会用本次结果覆盖共享区，复活已登出会话或把他 tab 翻回本账号
    syncAuthFromSharedStorage();
    // CAS：发起后 token 变了 → 丢弃，绝不 setSession（防复活已清会话 / 防 loser 覆盖 winner 新会话）
    if (tokenChanged()) return { status: 'superseded' };
    // keepEpoch：refresh 是同会话内 token 轮换，保持当前 epoch（区别于登录页建立新会话会自增 epoch）
    useAuthStore
      .getState()
      .setSession(
        { access: body.data.access, refresh: body.data.refresh, user: body.data.user },
        { keepEpoch: true },
      );
    return { status: 'ok', accessToken: body.data.access };
  } catch {
    // 已拿到 HTTP 响应之后的失败：body 解析错 / body 卡死被 abort / setSession 持久化被拒
    // → 瞬时故障，保留会话，但**不**标 networkFailure（不是连不上服务器）
    return { status: 'transient' };
  } finally {
    clearTimeout(timer);
  }
}

type RefreshStart = {
  /** 发起续期时本 tab 的 access（已失效的那张）；锁内发现 store 里换成了别的 = 他 tab 已续期 */
  accessAtStart: string | null;
  epochAtStart: number;
};

/**
 * 共享会话已替本 tab 给出结论时返回它，否则 null（需本 tab 自己续期 / 维持原结论）：
 * - epoch 变了 = 他 tab 登出 / 换号已同步进来：本 tab 已无会话 → invalid，已换成新会话 → superseded；
 * - 同会话但 access 已不是发起时那张 = 他 tab 已续期并共享 → 直接用，不再轮换（再轮换会作废他 tab 刚拿到的
 *   access）。不按本机时钟判它是否过期：时钟偏快会把新 token 判过期而多轮换、偏慢会放过旧 token；
 *   万一是旧值（如 bfcache 恢复后对齐到的过期 access），由 authedFetch 在它被拒后再真续期一次兜住。
 */
function outcomeFromSharedSession(start: RefreshStart): RefreshOutcome | null {
  const current = useAuthStore.getState();
  if (current.sessionEpoch !== start.epochAtStart) {
    return current.refreshToken ? { status: 'superseded' } : { status: 'invalid' };
  }
  if (current.accessToken && current.accessToken !== start.accessAtStart) {
    return { status: 'ok', accessToken: current.accessToken, fromSharedSession: true };
  }
  return null;
}

/** 等 store 的 refresh token 离开 `fromRefreshToken`（他 tab 写入经 storage 事件送达），至多 `timeoutMs`。 */
function waitForSharedSessionChange(fromRefreshToken: string, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const unsubscribe = useAuthStore.subscribe((state) => {
      if (state.refreshToken !== fromRefreshToken) finish();
    });
    const timer = setTimeout(finish, timeoutMs);
    function finish() {
      clearTimeout(timer);
      unsubscribe();
      resolve();
    }
    // 订阅前已落地的写入（事件已错过或尚未派发）主动对齐一次
    syncAuthFromSharedStorage();
    if (useAuthStore.getState().refreshToken !== fromRefreshToken) finish();
  });
}

/**
 * 锁内续期：先以 localStorage 共享会话对齐本 tab（storage 事件可能还没送达），共享会话已给结论就不发请求；
 * 否则用共享区最新 refresh token 续期，被拒时先等他 tab 写入送达再判失效。
 */
async function refreshWithSharedSession(start: RefreshStart): Promise<RefreshOutcome> {
  syncAuthFromSharedStorage();
  const shared = outcomeFromSharedSession(start);
  if (shared) return shared;
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return { status: 'invalid' };

  const outcome = await postRefresh(refreshToken);
  if (outcome.status === 'superseded') {
    // 在途 token 变了：仅「同会话他 tab 已续期」可直接复用其 access；登出 / 换号维持 superseded（不复活、不登出）
    const shared = outcomeFromSharedSession(start);
    return shared?.status === 'ok' ? shared : outcome;
  }
  if (outcome.status !== 'invalid') return outcome;
  await waitForSharedSessionChange(refreshToken, SHARED_SESSION_SETTLE_MS);
  const settled = outcomeFromSharedSession(start);
  if (settled) return settled;
  // 共享区已换成新 refresh、却没带新 access（旧版本 tab 只落 refresh）→ 用新 refresh 再续一次
  const latestRefreshToken = useAuthStore.getState().refreshToken;
  if (latestRefreshToken && latestRefreshToken !== refreshToken) {
    return postRefresh(latestRefreshToken);
  }
  return outcome;
}

/**
 * 同源所有 tab 串行执行续期（Web Locks）。
 * - 等锁超时 → transient：不无锁续期，否则会与持锁 tab 并发轮换同一 refresh token、败方被判失效；
 * - 其他拿锁失败（如不安全上下文的 SecurityError）→ 退化为不加锁执行。
 * task 不抛（postRefresh 已把所有失败分类成 RefreshOutcome）。
 */
async function withCrossTabRefreshLock(
  task: () => Promise<RefreshOutcome>,
): Promise<RefreshOutcome> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks?.request) return task();
  const lockWait = new AbortController();
  const lockWaitTimer = setTimeout(() => lockWait.abort(), REFRESH_LOCK_WAIT_MS);
  let granted = false;
  try {
    return await locks.request(REFRESH_LOCK_NAME, { signal: lockWait.signal }, () => {
      granted = true;
      clearTimeout(lockWaitTimer);
      return task();
    });
  } catch {
    if (granted || lockWait.signal.aborted) return { status: 'transient' };
    return task();
  } finally {
    clearTimeout(lockWaitTimer);
  }
}

/**
 * 用当前持久化的 refresh token 换新 access（typed 结果，见 RefreshOutcome）。
 * 同一 token 并发调用共享 in-flight promise；跨 tab 经 Web Locks 串行，他 tab 已续期则直接复用其结果。
 */
export async function refreshAccessToken(): Promise<RefreshOutcome> {
  const { refreshToken, accessToken, sessionEpoch } = useAuthStore.getState();
  if (!refreshToken) return { status: 'invalid' };
  if (!inFlight || inFlight.token !== refreshToken) {
    const start: RefreshStart = { accessAtStart: accessToken, epochAtStart: sessionEpoch };
    const promise = withCrossTabRefreshLock(() => refreshWithSharedSession(start));
    inFlight = { token: refreshToken, promise };
    void promise.finally(() => {
      if (inFlight?.promise === promise) inFlight = null;
    });
  }
  return inFlight.promise;
}

/**
 * forceLogout — 鉴权强制登出（401 / refresh 显式失效，非用户主动点登出）。
 * 清登录态 + 清 query 业务缓存（防同浏览器换运营读到上一个账户的非账户隔离 query 数据）。
 * `reason` 仅用于排障留痕（控制台），便于运营反馈「为何被登出」。
 */
export function forceLogout(reason: string): void {
  useAuthStore.getState().clearSession();
  queryClient.clear();
  console.warn(JSON.stringify({ event: 'admin.auth.force_logout', reason }));
}

/**
 * 瞬时故障 / 换号场景返回的合成 503——区别于真 401，避免 `httpStatus===401` 自登出的调用方
 * （如 ProfilePage 撤销会话 onError）在「续期暂时失败」或「请求所属会话已被换掉」时误踢运营。
 */
function transientResponse(): Response {
  return new Response(
    JSON.stringify({
      code: 'SERVICE_UNAVAILABLE',
      message: '会话续期暂时不可用，请稍后重试',
      requestId: '',
    }),
    { status: 503, headers: { 'Content-Type': 'application/json' } },
  );
}

/**
 * 无幂等请求的 401 续期分支：可以刷新 session，绝不重放 original request。
 * 续期成功/同会话 sibling 已续期时仍返回首个 401，交由调用方分类并等待显式人工重试。
 */
async function refreshSessionWithoutReplaying({
  response,
  bearerOverride,
  firstToken,
  sameSession,
  signal,
}: {
  response: Response;
  bearerOverride: string | undefined;
  firstToken: string | null | undefined;
  sameSession: () => boolean;
  signal?: AbortSignal;
}): Promise<Response> {
  if (response.status !== 401 || bearerOverride != null || !firstToken) return response;
  if (!sameSession()) return transientResponse();

  // 迟到的 stale-token 401：同会话 sibling 已完成续期，无需用新 refresh token 再轮换一次。
  const currentToken = useAuthStore.getState().accessToken;
  if (currentToken && currentToken !== firstToken) return response;

  const outcome = await raceAbort(refreshAccessToken(), signal);
  if (!sameSession()) return transientResponse();
  if (outcome.status === 'invalid') {
    forceLogout('refresh_invalid');
    return response;
  }
  // ok / transient / 同会话 superseded 都不改变首个 POST 401 的确定性；保留原响应供调用方分类。
  return response;
}

/**
 * authedFetch — 带 access token 注入 + 401→refresh→重试一次 的鉴权 fetch（admin 三处鉴权 fetch 统一入口）。
 * - bearerOverride（verify-2fa / bind-totp 临时 token）：用它且**不刷新、不登出**（临时 token 无对应 refresh）。
 * - replayOnUnauthorized=false：401 可续期 session，但原请求不重放；供无幂等写请求等待显式人工重试。
 * - 否则取 store.accessToken；收 401 时：
 *   ⓿ 会话代际守卫——以发起时的 sessionEpoch 为锚（仅 clearSession 自增）：发起后 epoch 变了
 *      （登出 / 换号 / 重登都先 clearSession）→ 本请求属已拆会话，绝不 refresh / 不 replay（防旧请求用新会话执行）/
 *      不登出新会话，返 503；
 *   ① 同会话（epoch 未变）：sibling 并发请求可能已刷新出新 access → 先用它重试一次，省冗余轮换；
 *   ② 仍需续期：ok→用新 token 重试；其中复用他 tab 共享 access 的 ok（fromSharedSession）被拒时，store 已有
 *      别的新 access 就用它重试，仍是这张就再真续期一次；invalid（postRefresh 已确认会话真失效）→ forceLogout 返 401；
 *      superseded（同会话内多为 sibling 轮换）→ epoch 仍未变且有未被拒过的新 access 则再试一次、否则 503；transient→503。
 * 至多两次续期 + 三次重试（① / 共享 access / 续期后），无递归死循环。登出决策由本函数统一拥有（调用方不再各自 clearSession）。
 *
 * 注：用 sessionEpoch（而非 user.id / refresh token）作会话锚——user.id 在同账号重登时不变(漏判，bot #4)；
 * refresh token 在同会话合法轮换时也变(误判 → 把正当 sibling 轮换当换号、mutation 误 503，codex)。
 * sessionEpoch 只在「会话拆除」自增，精确区分「同会话轮换」与「登出/重登」，两类问题一并解决。
 */
export async function authedFetch(
  input: string,
  init: RequestInit = {},
  opts: {
    bearerOverride?: string;
    replayOnUnauthorized?: boolean;
    /** 仅在调用方没传 signal 时生效；省略 = 默认 30s。fetchEnvelope 自带并集 controller，不会走到这里。 */
    timeoutMs?: number;
  } = {},
): Promise<Response> {
  // 直连调用方（CSV / 头像 / 封面）若不传 signal，自装默认超时，避免半开连接锁死那些 UI。
  // 不在返回时清 timer：headers 已到、body 还在读时 abort 仍能掐掉 json() / arrayBuffer()。
  // 调用方 signal（卸载取消）与硬超时并集：有 signal 也不能把 30s 顶掉。
  const timeoutMs = opts.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
  const composed = composeHardTimeout(init.signal, timeoutMs);
  const requestInit: RequestInit = { ...init, signal: composed.signal };
  // 在飞写请求登记（#356）**放在这一层、不放 apiRequest**：CSV 上传 / 校验、头像上传等直连调用方
  // 绕过 `apiRequest` 直接进这里，登记在上层会漏掉它们 —— 而 multipart 直传恰恰是最容易卡住、
  // 最需要「停止等待」出口的那批。本层是所有带鉴权请求的唯一公共入口（refresh 自身走裸 fetch、
  // 与通用拦截解耦，故不会被重复登记）。
  const method = init.method ?? 'GET';
  // 只有「通知条点了停止等待」这一种中止由本层分类：调用方自己传 signal 时（`client.ts` 恒传，
  // 它靠自己的 timer 分辨超时 vs 取消），本层把 AbortError 原样上抛，让它按自己的口径判。
  // 不区分的话，`client.ts` 的硬超时会被本层误判成「取消」——它的 signal 触发时，本层自己的
  // 计时器并没有到点。
  let abortedByNotice = false;
  const unregisterInflight =
    method.toUpperCase() === 'GET'
      ? undefined
      : registerInflightWrite({
          method: method.toUpperCase(),
          path: safePathOf(input),
          startedAt: performance.now(),
          abort: () => {
            abortedByNotice = true;
            composed.abort();
          },
        });
  // 兜底释放：中止 / 超时时立刻放掉登记，不留僵尸条目。
  if (unregisterInflight) {
    composed.signal.addEventListener('abort', unregisterInflight, { once: true });
  }
  try {
    const res = await runAuthedFetch(input, init, requestInit, opts);
    // **不在这里释放**：`fetch` 在响应头到达就 resolve，而 `apiRequest` / multipart helper 随后才读
    // `res.json()`。在此释放会让「头到了、body 卡住」的半开连接失去通知条与取消按钮 —— 那正是
    // 原实现把 `res.json()` 留在超时窗内所要防的同一件事。改为读 body 时才释放。
    return unregisterInflight
      ? releaseOnBodyConsumed(res, unregisterInflight, () => abortedByNotice, method)
      : res;
  } catch (err) {
    unregisterInflight?.();
    if ((err as { name?: string } | null)?.name === 'AbortError') {
      if (composed.didTimeout())
        throw new AdminRequestTimeoutError(init.method ?? 'GET', timeoutMs);
      // 直连调用方（CSV 校验 / 导入、头像上传）不经 client.ts，通知条中止它们时若只上抛原始
      // DOMException，调用方拿不到 `serverStateUnknown` —— 而写请求此刻**可能已经落库**，
      // UI 只能报个泛化失败。故本层负责分类**自己发起的**那次中止。
      if (abortedByNotice) throw new AdminRequestCanceledError(init.method ?? 'GET');
    }
    throw err;
  }
}

const BODY_METHODS = new Set(['json', 'text', 'arrayBuffer', 'blob', 'formData']);

/**
 * body 读取宽限窗。要读 body 的调用方都是 `await authedFetch(...)` 之后紧接着读，远快于此；
 * 超过这个窗口还没碰 body，即判定调用方**只看 status 就结束了**（如 `ProfileAvatar` 上传成功后
 * 只判 `res.ok`），立即释放登记。
 *
 * **必须远小于 `STUCK_THRESHOLD_MS`（8s）**：否则这类「其实早已成功」的请求会挂到通知条上，
 * 给运营一个假的「已等待 N 秒仍未返回」——比没有通知条更糟。
 */
const BODY_READ_GRACE_MS = 1_000;

/**
 * 包住响应的 body 读取方法：读完（无论成败）才释放在飞登记；宽限窗内没人碰 body 就自行释放。
 *
 * 用 Proxy 而非重建 `Response`：重建要把 status / headers / redirected / url 等逐一搬过去，
 * 漏一个就是静默的行为差异；且 body 只能被消费一次，重建必然多读一遍。
 */
function releaseOnBodyConsumed(
  res: Response,
  release: () => void,
  abortedByNotice: () => boolean,
  method: string,
): Response {
  let settled = false;
  const finish = () => {
    if (settled) return;
    settled = true;
    clearTimeout(graceTimer);
    release();
  };
  const graceTimer = setTimeout(finish, BODY_READ_GRACE_MS);
  return new Proxy(res, {
    get(target, prop) {
      // **不能用 `Reflect.get(target, prop, receiver)`**：那会把 proxy 当 `this` 交给原生访问器，
      // 而 `Response.status` / `.ok` 等要读内部私有槽，拿到 proxy 就抛 TypeError。
      // 直接在 target 上取属性 → 原生 getter 的 `this` 就是真 Response。
      const value = (target as unknown as Record<string | symbol, unknown>)[prop];
      if (typeof value !== 'function') return value;
      const bound = (value as (...a: unknown[]) => unknown).bind(target);
      if (!BODY_METHODS.has(prop as string)) return bound;
      return async (...args: unknown[]) => {
        clearTimeout(graceTimer); // 开始读 body → 释放时机改由这次读取接管，可长于宽限窗
        try {
          return await bound(...args);
        } catch (err) {
          // 头已到达、body 读到一半时点「停止等待」：中止发生在 `authedFetch` 返回**之后**，
          // 它那个 catch 已经不在栈上了。分类必须在这里补，否则直连调用方（如
          // 头像上传）会把裸 AbortError 吞成 INTERNAL_ERROR —— 而写请求
          // 此刻可能已落库，运营被告知的是错的。
          if ((err as { name?: string } | null)?.name === 'AbortError' && abortedByNotice()) {
            throw new AdminRequestCanceledError(method);
          }
          throw err;
        } finally {
          finish();
        }
      };
    },
  });
}

/**
 * 取 URL 的路径部分进通知条。**不带 query** —— 筛选值 / 游标进 UI 只是噪音，且可能含运营输入。
 * `input` 在各调用方之间形态不一（相对路径 / 绝对 URL），解析失败就原样退回，不因取个展示串抛错。
 */
function safePathOf(input: string): string {
  try {
    return new URL(input, window.location.origin).pathname;
  } catch {
    return input;
  }
}

async function runAuthedFetch(
  input: string,
  init: RequestInit,
  requestInit: RequestInit,
  opts: {
    bearerOverride?: string;
    replayOnUnauthorized?: boolean;
    timeoutMs?: number;
  },
): Promise<Response> {
  const withAuth = (token: string | null | undefined): RequestInit => ({
    ...requestInit,
    headers: { ...init.headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  const override = opts.bearerOverride;
  // 发起时的会话代际锚——仅 clearSession 自增，同会话内 refresh token 轮换不变它
  const initiatingEpoch = useAuthStore.getState().sessionEpoch;
  const sameSession = (): boolean => useAuthStore.getState().sessionEpoch === initiatingEpoch;
  const firstToken = override ?? useAuthStore.getState().accessToken;
  const res = await fetchOrThrowNetworkError(input, withAuth(firstToken));

  if (opts.replayOnUnauthorized === false) {
    return refreshSessionWithoutReplaying({
      response: res,
      bearerOverride: override,
      firstToken,
      sameSession,
      signal: requestInit.signal ?? undefined,
    });
  }

  // 非 401 直接返回；临时 token(override) 不续期；**未带 access token** 的请求（公开端点 / 未登录）的 401
  // 不是「access 过期」→ 不触发刷新（防把登录失败等公开 401 当过期 → 误刷旧会话 / 误转 503，bot #A）
  if (res.status !== 401 || override != null || !firstToken) return res;

  // ⓿ 会话代际守卫：发起后会话被拆过（登出/换号/重登都先 clearSession → epoch 自增）→ 旧请求不 refresh/不 replay/
  // 不登出新会话，当瞬时错误返回让调用方重试（防跨会话 replay、防旧请求 401 误登出新会话）
  if (!sameSession()) return transientResponse();

  // ① 同会话：sibling 并发请求可能已刷新出新 access → 先用它重试一次，省冗余轮换
  const current = useAuthStore.getState().accessToken;
  if (current && current !== firstToken) {
    const retry = await fetchOrThrowNetworkError(input, withAuth(current));
    if (retry.status !== 401) return retry;
  }

  // 重试收尾：刚拿到新 access 的 replay 仍 401 ⟹ 会话已被服务端撤销（session_version bump / 账号移除等）→
  // forceLogout（client.ts 已不在 401 时清登录态，此处不收口会让 UI 带失效 token 继续渲染）。仅当 epoch 未变才登出，
  // epoch 变=replay 期间会话已被换，401 属旧请求 → 不动新会话、返 503。
  // replay 期间共享 access 又被他 tab 轮换（store 里已不是 replay 用的那张）→ 401 只说明那张被作废、
  // 不是会话失效 → 同样不登出、返 503。
  const finishReplay = (replay: Response, replayToken: string): Response => {
    if (replay.status === 401) {
      if (sameSession() && useAuthStore.getState().accessToken === replayToken) {
        forceLogout('replay_rejected');
        return replay;
      }
      return transientResponse();
    }
    return replay;
  };

  // ② 自己续期（refreshAccessToken in-flight 去重）。
  // 与 caller signal 竞速、但不 abort 共享 refresh：超时 / 取消只停止本等待者。
  let outcome = await raceAbort(refreshAccessToken(), requestInit.signal ?? undefined);
  // 已确认被服务端拒绝的共享 access：之后的 superseded 兜底不再拿它重放
  let rejectedSharedAccess: string | null = null;
  if (outcome.status === 'ok' && outcome.fromSharedSession) {
    // 复用的是他 tab 写入共享区的 access，可能已过期（如 bfcache 恢复后对齐到的旧值），被拒不等于会话失效
    const replay = await fetchOrThrowNetworkError(input, withAuth(outcome.accessToken));
    if (replay.status !== 401 || !sameSession()) return finishReplay(replay, outcome.accessToken);
    rejectedSharedAccess = outcome.accessToken;
    const latest = useAuthStore.getState().accessToken;
    if (latest && latest !== outcome.accessToken && latest !== firstToken) {
      // 重放途中同 tab 兄弟请求已真续期出新 access → 直接用它，不再轮换
      return finishReplay(await fetchOrThrowNetworkError(input, withAuth(latest)), latest);
    }
    // store 里仍是这张 → 再走一轮真续期（此时它就是发起时那张，不会再被复用）
    outcome = await raceAbort(refreshAccessToken(), requestInit.signal ?? undefined);
  }
  if (outcome.status === 'ok') {
    return finishReplay(
      await fetchOrThrowNetworkError(input, withAuth(outcome.accessToken)),
      outcome.accessToken,
    );
  }
  if (outcome.status === 'invalid') {
    // 会话代际已变（他 tab 登出 / 换号已同步进本 tab）→ 本请求属旧会话，不再动当前会话
    if (!sameSession()) return transientResponse();
    // 同会话 invalid ⟹ 已用共享区最新 refresh token 续期仍被拒、且等待窗口内无他 tab 新会话送达 → 登出安全
    forceLogout('refresh_invalid');
    return res; // 真 401
  }
  if (outcome.status === 'superseded') {
    // 同会话内 superseded 多为 sibling 轮换 → 用当前新 access 再试一次（仍同会话且有新 token）
    const latest = useAuthStore.getState().accessToken;
    if (sameSession() && latest && latest !== firstToken && latest !== rejectedSharedAccess) {
      return finishReplay(await fetchOrThrowNetworkError(input, withAuth(latest)), latest);
    }
  }
  // 续期请求自身没拿到响应（断网 / 域名被阻断）→ 与首个请求断网同样归类，不压成 503 的
  // 「服务暂时不可用」（那会把本地网络问题说成服务端故障）。会话保持不变，仍不登出。
  if (outcome.status === 'transient' && outcome.networkFailure) {
    throw new AdminNetworkError();
  }
  // 其余 transient（5xx / 超时 / 解析错）/ 无新 token 可用 → 不登出、不 replay，返 503 让调用方按瞬时错误重试
  return transientResponse();
}
